import * as React from 'react'
import { observer } from 'mobx-react'
import cx from 'classnames'
import Avatar, { Badge } from 'components/Avatar'
import { useIsElementInView } from 'hooks/viewport-hooks'
import truncate from 'lodash/truncate'
import MessageContent from 'messaging/components/ConversationView/Messages/List/MessageContent'
import { MessageType } from 'messaging/models/MessageType'
import { Id } from 'types/definitions'
import { MessageGroup, MessageGroupTag } from '../../ConversationViewModel'
import MessageViewModel from '../../MessageViewModel'
import styles from './MessageGroup.sass'

const timeFormatter = new Intl.DateTimeFormat('en-US', {
  hour: 'numeric',
  minute: '2-digit',
  hour12: true,
})

const datelineFormatter = new Intl.DateTimeFormat('en-US', {
  weekday: 'long',
  month: 'long',
  day: 'numeric',
})

interface Props extends React.HTMLAttributes<HTMLDivElement> {
  group: MessageGroup
  onMarkAsRead: (message: MessageViewModel) => unknown
}

export default observer(function MessageGroup({ group, onMarkAsRead }: Props) {
  return (
    <div className={cx(styles.root)}>
      {group.showDateLine && (
        <div className={styles.dateline}>
          <div className={styles.datelineStroke} />
          <div className={styles.datelineValue}>
            {datelineFormatter.format(group.timestamp)}
          </div>
          <div className={styles.datelineStroke} />
        </div>
      )}
      <div className={styles.messages}>
        {group.messages.map((message, i) => (
          <Message
            group={group}
            key={Id.assert(message.message.id)}
            message={message}
            first={i === 0}
            onMarkAsRead={onMarkAsRead}
          />
        ))}
      </div>
    </div>
  )
})

interface MessageProps {
  message: MessageViewModel
  first: boolean
  group: MessageGroup
  onMarkAsRead: (message: MessageViewModel) => unknown
}

const Message = observer(function Message({
  message,
  first,
  group,
  onMarkAsRead,
}: MessageProps) {
  const messageRef = React.useRef<HTMLDivElement | null>(null)
  useIsElementInView(messageRef, (isInView) => {
    if (isInView) {
      onMarkAsRead(message)
    }
  })
  return (
    <div
      ref={messageRef}
      className={cx(
        styles.message,
        message.messageType === MessageType.Note && styles.isNote,
      )}
    >
      <div className={styles.gutter}>
        {first ? (
          <div className={styles.avatarContainer}>
            <Avatar
              size={38}
              subject={group.assistant ?? group.sender}
              badge={group.sender.user.online ? Badge.Online : Badge.Offline}
            />
          </div>
        ) : (
          <div className={styles.secondaryTimestamp}>
            {timeFormatter.format(message.message.dateCreated)}
          </div>
        )}
      </div>
      <div className={styles.content}>
        {first ? (
          <div className={styles.header}>
            <p className={styles.title}>
              <span
                className={cx(
                  styles.displayName,
                  group.sender.isCurrentUser && styles.me,
                )}
              >
                {truncate(
                  group.assistant?.displayName ?? group.sender.displayName,
                  { length: 100 },
                )}
              </span>
              {group.tag !== MessageGroupTag.None && (
                <>
                  {' '}
                  <span className={styles.groupTag}>
                    {group.tag === MessageGroupTag.Admin
                      ? 'Admin'
                      : group.tag === MessageGroupTag.Assistant
                        ? 'BETA Assistant'
                        : 'System'}
                  </span>
                </>
              )}{' '}
              <span className={styles.timestamp}>
                {timeFormatter.format(group.timestamp)}
              </span>
            </p>
          </div>
        ) : null}
        <MessageContent data-test="message-content" message={message} />
      </div>
      <div className={styles.extra}>
        {message.messageType === MessageType.Note && (
          <div className={styles.messageTag}>PERSONAL NOTE</div>
        )}
      </div>
    </div>
  )
})
